import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  InterleavedBufferAttribute,
  Intersection,
  LineSegments,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Vector3,
} from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { Text } from 'troika-three-text';

import { consequetivePairIterator } from '../../utils/generators';
import { bufferAttributeUtils } from '../../utils/three/buffer-attribute';
import { isDefined } from '../../utils/variables';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import { getDefaultColorValueMap } from '../elements/design/colors';
import { DesignNode } from '../elements/design/design-node/design-node';
import { PointId } from '../elements/design/design-node/utils/point-node';
import { ElementObj } from '../elements/element-obj';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { renderOrders } from '../render-orders';
import {
  MouseButtonType,
  MouseInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { Coordinates, toVector2 } from '../utilities/math-helpers';
import { Pick, setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { PickingLine } from './picking-line';
import { PickingSphere } from './picking-sphere';
import { isAssetPickable } from './select-tool';
import { Tool } from './tool';
import { findShortestPathBetweenPointIdsDijkstra } from './utils/context-menu/design-tool/utils/find-shortest-path-between-points';
import { getPointIdArray } from './utils/context-menu/design-tool/utils/select';

export type ProfileSectionToolMode = 'none' | 'draw';
export type ProfileSectionMeasureMode = 'distance' | 'area';
export type ProfileSectionGraphMode = 'length-profile' | 'cross-section';

export type AssetMeshBVHData = {
  asset: ElementObj;
  id: string;
  color: Color;
  data: {
    mesh: Mesh<BufferGeometry>;
    bvh: MeshBVH;
  }[];
};

export class ProfileSectionTool extends Tool {
  private _enabled = false;
  private cursor = new PickingSphere();
  private profileLine = new PickingLine();
  private crossSectionPicker = new PickingLine();
  private crossSections = new PickingLine(
    new Color(1, 1, 1),
    'solid',
    renderOrders.pickers.pickingLineBackground,
  );
  private intersectedEdges = new PickingLine(new Color(1, 0, 0));
  private draftLine = new PickingLine(new Color(0, 0.6, 1), 'dashed');

  private _toolMode: ProfileSectionToolMode = 'draw';

  measureMode: ProfileSectionMeasureMode = 'distance';
  flattenProfileLine: boolean = false;

  get toolMode() {
    return this._toolMode;
  }
  set toolMode(value: ProfileSectionToolMode) {
    this.draftLine.visible = value === 'draw';
    this.cursor.visible = value === 'draw';
    this.profileLine.visible = value === 'none';
    this.intersectedEdges.visible = value === 'none';
    this.inputHandler.setCursor(value === 'draw' ? 'crosshair' : '');
    this._toolMode = value;
  }

  coordinates: Coordinates[] = [];
  crossSectionCoordinates: Coordinates[] = [];
  crossSectionLeftWidth: number = 10;
  crossSectionRightWidth: number = 10;
  distanceFromStart?: number;
  profileLineName?: string;
  lastInterval: number | undefined;

  constructor(inputHandler: InputHandler) {
    super('profile-section', inputHandler);
    this.visible = false;
    this.draftLine.setNonIndexedVertices([new Vector3(), new Vector3()]);
  }

  setProfileLine(worldPositions: Vector3[], mode: ProfileSectionGraphMode, assetName?: string) {
    this.profileLineName = assetName;
    this.coordinates = worldPositions.map((x) =>
      CoordinateManager.getInstance().localToProjectCoords(x),
    );
    this.toolMode = 'none';
    this.refreshLine();
    publish(SubscriptionTopic.ProfileSectionUpdated);

    if (mode === 'cross-section') {
      this.setCrossSectionsPreview(this.lastInterval);
    } else {
      this.crossSectionCoordinates = [];
      this.refreshCrossSections();
    }
  }

  setEdges(positions: Vector3[][]) {
    const mas = CoordinateManager.getInstance().getMAS();
    this.intersectedEdges.position.set(0, 0, -mas);
    this.intersectedEdges.setNonIndexedVertices(positions.flat());
  }

  private getCrossSection(a: Vector3, b: Vector3, t: number) {
    const crossSectionCenter = a.clone().lerp(b, t);
    const direction2d = b.clone().sub(a).setZ(0).normalize();

    // 90° rotation CCW
    const perpendicularVector = new Vector3(-direction2d.y, direction2d.x, 0);

    const start = crossSectionCenter
      .clone()
      .add(perpendicularVector.clone().multiplyScalar(this.crossSectionLeftWidth));
    const end = crossSectionCenter
      .clone()
      .sub(perpendicularVector.clone().multiplyScalar(this.crossSectionRightWidth));

    return [
      { easting: start.x, northing: start.y, height: start.z },
      { easting: end.x, northing: end.y, height: end.z },
    ];
  }

  setCrossSectionsPreview(interval?: number) {
    this.lastInterval = interval;
    this.crossSectionCoordinates = [];

    if (!isDefined(interval) || this.coordinates.length === 0) {
      this.crossSections.visible = false;
      this.crossSectionPicker.visible = false;
      return;
    }

    let totalLength = 0;
    let nextOffset = 0;

    const coordinatePairs = [
      ...consequetivePairIterator(
        this.coordinates.map(
          (x) => new Vector3(x.easting, x.northing, this.flattenProfileLine ? 0 : x.height),
        ),
      ),
    ];

    for (const [a, b] of coordinatePairs) {
      const segmentLength = a.distanceTo(b);

      while (segmentLength > 0 && totalLength + segmentLength >= nextOffset) {
        const t = (nextOffset - totalLength) / segmentLength;
        this.crossSectionCoordinates.push(...this.getCrossSection(a, b, t));
        nextOffset += interval;
      }

      totalLength += segmentLength;
    }

    // Always push last segment.
    const lastSegment = coordinatePairs.at(-1)!;
    this.crossSectionCoordinates.push(...this.getCrossSection(lastSegment[0], lastSegment[1], 1));

    this.refreshCrossSections();

    this.crossSections.visible = true;
  }

  /**
   * Gets either the length profile or a cross section of the length profile based on the offset.
   */
  getProfileLine(mode: ProfileSectionGraphMode, offset = 0): Coordinates[] {
    if (mode === 'length-profile') {
      this.distanceFromStart = undefined;
      return this.coordinates.map((x) => ({
        easting: x.easting,
        northing: x.northing,
        height: this.flattenProfileLine ? 0 : x.height,
      }));
    }

    this.distanceFromStart = offset;

    let totalLength = 0;
    for (const [a, b] of consequetivePairIterator(
      this.coordinates.map(
        (x) => new Vector3(x.easting, x.northing, this.flattenProfileLine ? 0 : x.height),
      ),
    )) {
      const segmentLength = a.distanceTo(b);

      if (segmentLength > 0 && totalLength + segmentLength >= offset) {
        const t = (offset - totalLength) / segmentLength;
        return this.getCrossSection(a, b, t);
      }

      totalLength += segmentLength;
    }

    // Offset exceeds the profile line length.
    return [];
  }

  enabled(): boolean {
    return this._enabled;
  }

  private bvhMap: Map<string, MeshBVH> = new Map();
  private assetColorMap: Map<string, Color> = new Map();

  setAssetColor(id: string, color: Color) {
    this.assetColorMap.set(id, color);
  }

  getBvh(mesh: Mesh<BufferGeometry>) {
    const existing = this.bvhMap.get(mesh.uuid);
    if (existing) {
      return existing;
    }

    const bvh = new MeshBVH(mesh.geometry, { maxLeafTris: 32 });
    this.bvhMap.set(mesh.uuid, bvh);

    return bvh;
  }

  getAssets(): AssetMeshBVHData[] {
    const assets = AssetManager.instance
      .getAssets()
      .filter((x) => x.getStateVisibility() && x.loadingStatus === 'loaded');

    const defaultColors = [...getDefaultColorValueMap().colorMap.values()];

    const result: AssetMeshBVHData[] = [];
    for (const asset of assets) {
      const meshes: Mesh<BufferGeometry>[] = [];

      // Traverse the asset and get all the meshes but exclude Troika text objects.
      asset.traverse((child) => {
        if (
          child instanceof Mesh &&
          child.geometry instanceof BufferGeometry &&
          (child.material as MeshBasicMaterial)?.wireframe !== true &&
          !(child instanceof Text)
        ) {
          meshes.push(child as Mesh<BufferGeometry>);
        }
      });

      const id = asset.assetId ?? asset.uuid;
      let color = this.assetColorMap.get(id);

      if (!isDefined(color)) {
        color = defaultColors[this.assetColorMap.size % defaultColors.length];
        this.assetColorMap.set(id, color);
      }

      if (meshes.length > 0) {
        result.push({
          asset,
          id,
          color,
          data: meshes.map((x) => ({
            mesh: x,
            bvh: this.getBvh(x),
          })),
        });
      }
    }

    return result;
  }

  enable() {
    this._enabled = true;
    this.visible = true;
    this.coordinates = [];
    this.toolMode = 'draw';
    this.measureMode = 'distance';
    this.profileLineName = undefined;
    this.bvhMap.clear();
    this.refreshLine();
    this.add(this.crossSectionPicker);
    this.add(this.crossSections);
    this.add(this.intersectedEdges);
    this.add(this.draftLine);
    this.add(this.profileLine);
    this.add(this.cursor);
  }

  disable() {
    this._enabled = false;
    this.visible = false;
    this.coordinates = [];
    this.profileLineName = undefined;
    this.bvhMap.clear();
    this.inputHandler.setCursor('');
    this.remove(this.crossSectionPicker);
    this.remove(this.crossSections);
    this.remove(this.intersectedEdges);
    this.remove(this.draftLine);
    this.remove(this.profileLine);
    this.remove(this.cursor);
  }

  refreshCrossSections() {
    const crossSectionVertices: Vector3[] = this.crossSectionCoordinates.map((x) =>
      CoordinateManager.getInstance().projectCoordsToLocal(x),
    );

    this.crossSections.position.set(0, 0, 0);
    this.crossSections.setNonIndexedVertices(crossSectionVertices);
  }

  refreshLine(currentPoint?: Vector3) {
    this.profileLine.position.set(0, 0, 0);
    this.draftLine.position.set(0, 0, 0);

    if (this.coordinates.length < 1) {
      this.profileLine.setNonIndexedVertices([]);
      this.draftLine.setNonIndexedVertices([]);
      return;
    }

    const points = [
      ...consequetivePairIterator(
        this.coordinates.map((x) => CoordinateManager.getInstance().projectCoordsToLocal(x)),
      ),
    ].flat();

    this.profileLine.setNonIndexedVertices(points);

    // Push the predicted segment for the draft line.
    if (currentPoint) {
      points.push(
        CoordinateManager.getInstance().projectCoordsToLocal(this.coordinates.at(-1)!),
        currentPoint,
      );
    }

    this.draftLine.setNonIndexedVertices(points);
  }

  setCursorPosition(position?: Vector3) {
    this.cursor.visible = isDefined(position);

    if (position) {
      const newPosition = position
        .clone()
        .setZ(position.z - CoordinateManager.getInstance().getMAS());
      this.cursor.position.copy(newPosition);
    }
  }

  pick(mouseInputData: MouseInputData) {
    const camera = cameraManager().getActiveCamera();

    if (!camera) {
      return undefined;
    }

    const rayCast = camera.getRay(
      mouseInputData.pointer.position.x,
      mouseInputData.pointer.position.y,
    );

    const pixelSnapThreshold = 20;
    const minPickingDistance = 0.1;
    const maxPickingDistance = 350;

    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCast,
      pixelSnapThreshold,
      MathUtils.clamp(
        this.cursor.position.distanceTo(rayCast.ray.origin),
        minPickingDistance,
        maxPickingDistance,
      ),
    );

    if (!this.inputHandler.shiftIsDown) {
      const intersections = Pick.instance.assets(
        mouseInputData.pointer.position.x,
        mouseInputData.pointer.position.y,
        isAssetPickable,
        {
          pixelSortScreenPosition: {
            client: toVector2(
              camera.getScreenPosition(rayCast.ray.origin.clone().add(rayCast.ray.direction)),
            ),
          },
          rayCast,
          skipPrioritizePointSnapping: true,
        },
      );

      const lineSegments = intersections.find(
        (x) =>
          x.object instanceof LineSegments &&
          x.object.geometry instanceof BufferGeometry &&
          (x.object.material as MeshBasicMaterial).wireframe !== true,
      );
      if (lineSegments) {
        const index = lineSegments.index;
        const object3d = lineSegments.object;
        const pickedPoint = lineSegments.point;
        if (
          object3d instanceof LineSegments &&
          object3d.geometry instanceof BufferGeometry &&
          isDefined(index)
        ) {
          const position = object3d.geometry.getAttribute('position');
          if (
            !position ||
            (!(position instanceof Float32BufferAttribute) &&
              !(position instanceof InterleavedBufferAttribute))
          ) {
            return undefined;
          }

          const a = object3d.localToWorld(bufferAttributeUtils.getVector3(index, position));
          const b = object3d.localToWorld(bufferAttributeUtils.getVector3(index + 1, position));

          this.lineSegments = object3d;
          return a.distanceToSquared(pickedPoint) < b.distanceToSquared(pickedPoint) ? a : b;
        }
      }
    }

    this.lineSegments = undefined;

    return Pick.instance.closestPoint(camera, rayCast, {
      skipAssets: true,
      pickHeight: 0,
    });
  }

  private lineSegments: LineSegments<BufferGeometry> | undefined;
  private lastUuid: string | undefined;
  private lastPointId: PointId | undefined;
  private designNode: DesignNode | undefined;

  resetProfileLine() {
    // Clear profile line content and draft line and set the first point of the draft line.
    this.setEdges([]);
    this.coordinates = [];
    this.crossSectionCoordinates = [];
    this.profileLineName = undefined;
    this.toolMode = 'draw';
    this.refreshLine();
    this.refreshCrossSections();
  }

  async onMouseUp(mouseInputData: MouseInputData) {
    if (this.mouseMoved(mouseInputData)) {
      return true;
    }

    const point = this.pick(mouseInputData);

    if (mouseInputData.button.type === MouseButtonType.RightButton) {
      if (this.toolMode === 'draw') {
        this.toolMode = 'none';
        this.lastPointId = undefined;
        if (this.coordinates.length > 1) {
          publish(SubscriptionTopic.ProfileSectionUpdated);
        }
      }
    }

    // If it's draw mode. Push a point regardless of which mouse button is clicked.
    // This way the first point will be pushed when initiating draw mode with right click too.
    if (this.toolMode === 'draw' && point) {
      if (isDefined(this.lineSegments)) {
        const sameUuid = this.lastUuid === this.lineSegments.uuid;

        const node =
          isDefined(this.designNode) && sameUuid
            ? this.designNode
            : new DesignNode([this.lineSegments], {
                mergeDuplicatePoints: true,
              });

        if (!sameUuid) {
          this.lastPointId = undefined;
        }

        let closestId = node.points[0];
        let closest = node.getPointById(closestId);
        let lastDistance = closest.vertex.distanceToSquared(point);

        for (const id of node.points) {
          const next = node.getPointById(id);
          const newDistance = next.vertex.distanceToSquared(point);
          if (newDistance < lastDistance) {
            closest = next;
            lastDistance = newDistance;
            closestId = id;
          }
        }

        if (
          this.coordinates.length > 0 &&
          isDefined(this.lastPointId) &&
          this.lastUuid === this.lineSegments.uuid &&
          this.lastPointId !== closestId
        ) {
          const path = findShortestPathBetweenPointIdsDijkstra(node, this.lastPointId, closestId);

          if (path) {
            const newPointIds = getPointIdArray(path).slice(1);
            const coordinates = newPointIds.map((x) =>
              CoordinateManager.getInstance().localToProjectCoords(node.getPointById(x).vertex),
            );
            this.coordinates.push(...coordinates);
          }
        } else {
          this.coordinates.push(
            CoordinateManager.getInstance().localToProjectCoords(closest.vertex),
          );
        }

        this.lastUuid = this.lineSegments.uuid;
        this.designNode = node;

        // return closest;
        this.lastPointId = closestId;
      } else {
        this.coordinates.push(CoordinateManager.getInstance().localToProjectCoords(point));
        this.lastPointId = undefined;
      }
    }

    // Pick cross section.
    if (
      mouseInputData.button.type === MouseButtonType.LeftButton &&
      this.toolMode === 'none' &&
      isDefined(this.lastInterval)
    ) {
      const index = this.refreshCrossSectionPicker(mouseInputData);
      if (isDefined(index)) {
        this.crossSectionPicker.visible = false;
        publish(SubscriptionTopic.ProfileSectionOffsetUpdated, (index / 2) * this.lastInterval);
      }
    }

    this.refreshLine(point);

    return Promise.resolve(true);
  }

  onMouseMove(mouseInputData: MouseInputData): boolean {
    this.cursor.visible = this.toolMode === 'draw';
    const point = this.pick(mouseInputData);

    if (!point) {
      return true;
    }

    this.cursor.position.copy(point);

    if (this.toolMode === 'draw') {
      this.refreshLine(point);
    }

    const pickedCrossSection = this.refreshCrossSectionPicker(mouseInputData);
    if (this.toolMode === 'none') {
      this.inputHandler.setCursor(isDefined(pickedCrossSection) ? 'pointer' : '');
    }

    return true;
  }

  refreshCrossSectionPicker(mouseInputData: MouseInputData) {
    const camera = cameraManager().getActiveCamera();
    if (
      !camera ||
      !isDefined(this.lastInterval) ||
      this.toolMode === 'draw' ||
      this.crossSectionCoordinates.length === 0
    ) {
      this.crossSectionPicker.visible = false;
      return undefined;
    }

    const rayCast = camera.getRay(
      mouseInputData.pointer.position.x,
      mouseInputData.pointer.position.y,
    );

    rayCast.params.Line = { threshold: this.lastInterval / 2 };
    const intersections = rayCast.intersectObject(this.crossSections, true);

    if (intersections.length > 0) {
      intersections.forEach((x: Intersection) => {
        (x as Intersection & { distanceToRay: number }).distanceToRay = rayCast.ray.distanceToPoint(
          x.point,
        );
      });
      const closestIntersection = intersections.sort(
        (a: any, b: any) => a.distanceToRay! - b.distanceToRay!,
      )[0];

      if (isDefined(closestIntersection?.index)) {
        const coordinateIndex = closestIntersection.index - (closestIntersection.index % 2);
        this.crossSectionPicker.setNonIndexedVertices([
          CoordinateManager.getInstance().projectCoordsToLocal(
            this.crossSectionCoordinates[coordinateIndex],
          ),
          CoordinateManager.getInstance().projectCoordsToLocal(
            this.crossSectionCoordinates[coordinateIndex + 1],
          ),
        ]);
        this.crossSectionPicker.position.set(0, 0, 0);
        this.crossSectionPicker.visible = true;
        return coordinateIndex;
      }
    }

    this.crossSectionPicker.visible = false;
  }

  onProjectProjectionUpdated() {
    const origoDifference = CoordinateManager.getInstance().origoDifference;
    this.cursor?.position.add(origoDifference);
    this.draftLine?.position.add(origoDifference);
    this.profileLine?.position.add(origoDifference);
    this.crossSections?.position.add(origoDifference);
    this.intersectedEdges?.position.add(origoDifference);
    this.crossSectionPicker?.position.add(origoDifference);
    return true;
  }
}
